import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import TradeConfigurationCard from './TradeConfigurationCard';

/**
 * SuggestedTradesTable - Enhanced trade display component for the Rebalancer Dashboard
 * 
 * Provides a more interactive trade view similar to the Rebalancer trade screen:
 * - Groups trades by security/sleeve with weight visualization bar
 * - Expandable rows for trade details and reasoning
 * - Account holdings overview per security group
 * - Trade Configuration style layout with editable fields
 */
const SuggestedTradesTable = ({
    trades,
    formatCurrency,
    accountAllocations = [],
    securityAllocations = [],
    allAccountHoldings = {},
    onQueueTrades = null,
    isQueuingTrades = false,
    queuedTradesData = null,
    showQueuedTrades = true,
    onToggleQueuedView = null,
    onQueueTriggerReady = null,  // Callback to expose the queue trigger function
    onAllocationsChange = null,
    onTradesChange = null         // Fires with flat trade list whenever edits change
}) => {
    const areAllocationRowsEqual = useCallback((prevRows = [], nextRows = []) => {
        if (prevRows === nextRows) return true;
        if (!Array.isArray(prevRows) || !Array.isArray(nextRows)) return false;
        if (prevRows.length !== nextRows.length) return false;

        for (let i = 0; i < prevRows.length; i += 1) {
            const prev = prevRows[i] || {};
            const next = nextRows[i] || {};
            if ((prev.name || prev.sleeveName || '') !== (next.name || next.sleeveName || '')) return false;
            if ((prev.beforeValue || prev.before_value || 0) !== (next.beforeValue || next.before_value || 0)) return false;
            if ((prev.afterValue || prev.after_value || 0) !== (next.afterValue || next.after_value || 0)) return false;
            if ((prev.tradeImpact || prev.trade_impact || 0) !== (next.tradeImpact || next.trade_impact || 0)) return false;
            if ((prev.beforeWeight || prev.before_weight || 0) !== (next.beforeWeight || next.before_weight || 0)) return false;
            if ((prev.afterWeight || prev.after_weight || 0) !== (next.afterWeight || next.after_weight || 0)) return false;
        }

        return true;
    }, []);

    const areAllocationPayloadsEqual = useCallback((prevPayload, nextPayload) => {
        if (prevPayload === nextPayload) return true;
        if (!prevPayload || !nextPayload) return false;
        return areAllocationRowsEqual(prevPayload.security || [], nextPayload.security || [])
            && areAllocationRowsEqual(prevPayload.sleeve || [], nextPayload.sleeve || []);
    }, [areAllocationRowsEqual]);

    const [expandedRows, setExpandedRows] = useState(new Set());
    const [reasoningModalData, setReasoningModalData] = useState(null); // { trade, groupKey }
    const [allTradesEdits, setAllTradesEdits] = useState({}); // { groupKey: [tradeData1, tradeData2, ...] }
    const [additionalModelGroups, setAdditionalModelGroups] = useState([]); // Array of model keys added by user
    const [showHoldingsOverview, setShowHoldingsOverview] = useState(false); // Collapsible holdings overview panel
    const [expandedHoldingsGroups, setExpandedHoldingsGroups] = useState(new Set()); // Which group cards are expanded in the holdings panel
    const [showRestrictedModels, setShowRestrictedModels] = useState(false); // Collapsible restricted models sub-panel
    const [showTradeCardForHoldings, setShowTradeCardForHoldings] = useState(new Set()); // Holdings groups with trade card open
    const [holdingsSortState, setHoldingsSortState] = useState({}); // { [groupKey]: { col, dir } } for holdings table sorting

    // Initialize allTradesEdits from original trades or queued trades when data changes
    const _lastInitSource = useRef(null);
    useEffect(() => {
        const initialEdits = {};

        // Determine which trades to use: queued trades (if viewing queued and available) or suggested trades
        const tradesToUse = (showQueuedTrades && queuedTradesData?.trades)
            ? queuedTradesData.trades
            : trades;

        const isQueuedData = showQueuedTrades && queuedTradesData?.trades;

        // Build a simple signature for the source so we only reinitialize when it truly changes
        const sourceSignature = JSON.stringify({
            showQueued: !!showQueuedTrades,
            queuedId: queuedTradesData?.group_name || queuedTradesData?.queued_at || null,
            count: (tradesToUse || []).length,
            totalValue: (tradesToUse || []).reduce((s, t) => s + Math.abs(t.value || t.trade_value || 0), 0)
        });

        // If the source hasn't changed and we already have edits, don't overwrite them.
        // Also bail when the source is empty (0 trades) — otherwise setting state to a
        // fresh {} every render causes an infinite update loop via onTradesChange.
        const sourceIsEmpty = (tradesToUse || []).length === 0;
        if (_lastInitSource.current === sourceSignature && (sourceIsEmpty || Object.keys(allTradesEdits || {}).length > 0)) {
            return;
        }

        tradesToUse.forEach((trade) => {
            // For queued trades, use group_key directly; for suggested trades, build the key
            const key = trade.group_key || (trade.sleeve_name && trade.security_model
                ? `${trade.sleeve_name}/${trade.security_model}`
                : (trade.sleeve_name || trade.security_model || trade.symbol || 'Unknown'));

            if (!initialEdits[key]) {
                initialEdits[key] = [];
            }

            // For queued trades, quantity is already signed correctly
            // For suggested trades, apply sign based on action
            const quantity = isQueuedData
                ? (trade.quantity || 0)
                : (trade.action === 'SELL' ? -(Math.abs(trade.quantity || 0)) : Math.abs(trade.quantity || 0));

            initialEdits[key].push({
                account_name: trade.account_name,
                account_number: trade.account_number,
                symbol: trade.symbol,
                quantity: quantity,
                price: trade.price || 0,
                action: trade.action || 'BUY',
                below_minimum_target: trade.below_minimum_target || false,
                security_type: trade.security_type || 'EQUITY',
                trading_security_type: trade.trading_security_type || ''
            });
        });

        setAllTradesEdits(initialEdits);
        _lastInitSource.current = sourceSignature;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trades, showQueuedTrades, queuedTradesData]);

    // Build a lookup of current holdings per account/symbol from:
    // 1. allAccountHoldings prop (fetched from API for all accounts)

    const holdingsLookup = useMemo(() => {
        const lookup = {};
        Object.entries(allAccountHoldings || {}).forEach(([accountNumber, holdings]) => {
            if (!Array.isArray(holdings)) return;
            holdings.forEach(h => {
                const symbol = h.Symbol || h.symbol;
                const qty = h.Quantity || h.quantity || 0;
                if (symbol) lookup[`${accountNumber}-${symbol}`] = qty;
            });
        });

        (trades || []).forEach(trade => {
            if (trade.account_number && trade.symbol) {
                const key = `${trade.account_number}-${trade.symbol}`;
                if (lookup[key] === undefined) {
                    const currentHoldings = trade.reasoning?.reasoning?.security_selection?.current_holdings
                        || trade.trade_reasoning?.reasoning?.security_selection?.current_holdings
                        || trade.reasoning?.security_selection?.current_holdings
                        || trade.trade_reasoning?.security_selection?.current_holdings
                        || 0;
                    lookup[key] = currentHoldings;
                }
            }
        });

        return lookup;
    }, [trades, allAccountHoldings]);

    // Build a symbol -> security type lookup from existing trades for auto-populating manual trades
    const symbolSecurityTypes = useMemo(() => {
        const lookup = {};
        (trades || []).forEach(trade => {
            if (trade.symbol && !lookup[trade.symbol]) {
                lookup[trade.symbol] = {
                    security_type: trade.security_type || 'EQUITY',
                    trading_security_type: trade.trading_security_type || ''
                };
            }
        });
        return lookup;
    }, [trades]);

    const updateTradeField = useCallback((groupKey, tradeIndex, field, value) => {
        setAllTradesEdits(prev => {
            const newEdits = { ...prev };
            if (!newEdits[groupKey]) newEdits[groupKey] = [];
            if (!newEdits[groupKey][tradeIndex]) newEdits[groupKey][tradeIndex] = {};

            if (typeof field === 'object' && field !== null) {
                newEdits[groupKey][tradeIndex] = { ...newEdits[groupKey][tradeIndex], ...field };
            } else {
                newEdits[groupKey][tradeIndex] = { ...newEdits[groupKey][tradeIndex], [field]: value };
            }

            if (field === 'quantity') {
                let bounded = value;
                if (value < 0) {
                    const accountNumber = newEdits[groupKey][tradeIndex].account_number;
                    const symbol = newEdits[groupKey][tradeIndex].symbol;
                    const lookupKey = `${accountNumber}-${symbol}`;
                    const currentHoldings = holdingsLookup[lookupKey] || 0;
                    bounded = Math.max(value, -currentHoldings);
                }
                const price = newEdits[groupKey][tradeIndex].price || 0;
                newEdits[groupKey][tradeIndex].quantity = bounded;
                newEdits[groupKey][tradeIndex].dollars = bounded * price;
                newEdits[groupKey][tradeIndex].action = bounded < 0 ? 'SELL' : bounded > 0 ? 'BUY' : 'HOLD';
            }

            if (field === 'dollars') {
                const price = newEdits[groupKey][tradeIndex].price || 0;
                if (price > 0) {
                    newEdits[groupKey][tradeIndex].quantity = value / price;
                    newEdits[groupKey][tradeIndex].action = value < 0 ? 'SELL' : value > 0 ? 'BUY' : 'HOLD';
                }
            }

            return newEdits;
        });
    }, [holdingsLookup]);

    const addTradeRow = useCallback((groupKey) => {
        setAllTradesEdits(prev => ({
            ...prev,
            [groupKey]: [...(prev[groupKey] || []), { account_name: '', account_number: '', symbol: '', quantity: 0, price: 0, action: 'BUY', security_type: 'EQUITY', trading_security_type: '', isAdded: true }]
        }));
    }, []);

    const removeTradeRow = useCallback((groupKey, tradeIndex) => {
        setAllTradesEdits(prev => {
            const newEdits = { ...prev };
            if (newEdits[groupKey]) newEdits[groupKey] = newEdits[groupKey].filter((_, i) => i !== tradeIndex);
            return newEdits;
        });
    }, []);

    const resetGroupTrades = useCallback((groupKey) => {
        const originalGroupTrades = (trades || []).filter(trade => {
            const key = trade.sleeve_name && trade.security_model ? `${trade.sleeve_name}/${trade.security_model}` : (trade.sleeve_name || trade.security_model || trade.symbol || 'Unknown');
            return key === groupKey;
        });

        setAllTradesEdits(prev => ({
            ...prev,
            [groupKey]: originalGroupTrades.map(trade => ({
                account_name: trade.account_name,
                account_number: trade.account_number,
                symbol: trade.symbol,
                quantity: trade.action === 'SELL' ? -(Math.abs(trade.quantity || 0)) : Math.abs(trade.quantity || 0),
                price: trade.price || 0,
                action: trade.action || 'BUY',
                security_type: trade.security_type || 'EQUITY',
                trading_security_type: trade.trading_security_type || ''
            }))
        }));
    }, [trades]);

    const addModelGroup = useCallback((modelName) => {
        if (!modelName || additionalModelGroups.includes(modelName)) return;
        setAdditionalModelGroups(prev => [...prev, modelName]);
        setAllTradesEdits(prev => ({ ...prev, [modelName]: [{ account_name: '', account_number: '', symbol: '', quantity: 0, price: 0, action: 'BUY', security_type: 'EQUITY', trading_security_type: '', isAdded: true }] }));
        setExpandedRows(prev => new Set(prev).add(modelName));
    }, [additionalModelGroups]);

    const removeModelGroup = useCallback((modelKey) => {
        setAdditionalModelGroups(prev => prev.filter(m => m !== modelKey));
        setAllTradesEdits(prev => {
            const n = { ...prev };
            delete n[modelKey];
            return n;
        });
    }, []);

    const collectAllTrades = useCallback(() => {
        const all = [];
        Object.entries(allTradesEdits).forEach(([groupKey, groupTrades]) => {
            const keyParts = groupKey.split('/');
            const sleeve_name = keyParts.length >= 2 ? keyParts[0] : groupKey;
            const security_model = keyParts.length >= 2 ? keyParts.slice(1).join('/') : null;

            groupTrades.forEach(trade => {
                if (trade.account_number && trade.symbol && trade.quantity !== 0) {
                    // Use trade.action as stored — do NOT re-derive from quantity sign.
                    // The visual badge and updateTradeField already keep action in sync.
                    all.push({ ...trade, group_key: groupKey, sleeve_name, security_model, trade_value: (trade.quantity || 0) * (trade.price || 0) });
                }
            });
        });
        return all;
    }, [allTradesEdits]);

    const handleQueueTradesClick = useCallback(() => {
        if (!onQueueTrades) return;
        const all = collectAllTrades();
        if (all.length === 0) {
            alert('No valid trades to queue. Please ensure trades have account, symbol, and quantity.');
            return;
        }
        onQueueTrades(all, accountAllocations, securityAllocations);
    }, [onQueueTrades, collectAllTrades, accountAllocations, securityAllocations]);

    useEffect(() => {
        if (onQueueTriggerReady) onQueueTriggerReady(handleQueueTradesClick);
    }, [onQueueTriggerReady, handleQueueTradesClick]);

    // Fire onTradesChange whenever the user edits trades so parent can update live cash/cards
    useEffect(() => {
        if (typeof onTradesChange !== 'function') return;
        onTradesChange(collectAllTrades());
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allTradesEdits]);

    const availableAccounts = useMemo(() => (accountAllocations || []).map(a => ({ name: a.accountName, number: a.accountNumber })), [accountAllocations]);

    const symbolsByGroup = useMemo(() => {
        const groupSymbols = {};
        (trades || []).forEach(trade => {
            const key = trade.sleeve_name && trade.security_model ? `${trade.sleeve_name}/${trade.security_model}` : (trade.sleeve_name || trade.security_model || trade.symbol || 'Unknown');
            if (!groupSymbols[key]) groupSymbols[key] = {};
            if (trade.symbol && !groupSymbols[key][trade.symbol]) groupSymbols[key][trade.symbol] = { symbol: trade.symbol, price: trade.price || 0, security_type: trade.security_type || 'EQUITY', trading_security_type: trade.trading_security_type || '', holdings: {} };
            if (trade.symbol && trade.account_number) {
                const lookupKey = `${trade.account_number}-${trade.symbol}`;
                const actualHoldings = holdingsLookup[lookupKey] || 0;
                groupSymbols[key][trade.symbol].holdings[trade.account_number] = { account_name: trade.account_name, quantity: actualHoldings };
            }
        });
        const res = {};
        Object.keys(groupSymbols).forEach(k => { res[k] = Object.values(groupSymbols[k]); });
        return res;
    }, [trades, holdingsLookup]);

    const symbolsByModel = useMemo(() => {
        const modelSymbols = {};
        Object.entries(allAccountHoldings || {}).forEach(([accountNumber, holdings]) => {
            if (!Array.isArray(holdings)) return;
            holdings.forEach(h => {
                // Use "Other Holdings" for securities without a model name (matches RebalancerDashboard grouping)
                // Backend labels cash symbols as "UNASSIGNED CASH" - skip those for Other Holdings
                const rawModelName = h['Security Level Model Name'] || h.modelName || '';
                const modelName = rawModelName && rawModelName.trim() !== '' ? rawModelName : 'Other Holdings';
                const symbol = h.Symbol || h.symbol;
                const qty = h.Quantity || h.quantity || 0;
                const price = h.Price || h['Price Per Unit'] || h.price || 0;
                if (!symbol) return;
                // Skip cash symbols (labeled by backend as "UNASSIGNED CASH") for Other Holdings
                if (modelName === 'Other Holdings' && rawModelName.includes('UNASSIGNED CASH')) return;
                if (!modelSymbols[modelName]) modelSymbols[modelName] = {};
                if (!modelSymbols[modelName][symbol]) {
                    const secTypes = symbolSecurityTypes[symbol] || {};
                    modelSymbols[modelName][symbol] = { symbol, price, security_type: secTypes.security_type || 'EQUITY', trading_security_type: secTypes.trading_security_type || '', holdings: {} };
                }
                modelSymbols[modelName][symbol].holdings[accountNumber] = qty;
                if (price > 0 && (!modelSymbols[modelName][symbol].price || modelSymbols[modelName][symbol].price === 0)) modelSymbols[modelName][symbol].price = price;
            });
        });
        const res = {}; Object.keys(modelSymbols).forEach(m => res[m] = Object.values(modelSymbols[m]));
        return res;
    }, [allAccountHoldings, symbolSecurityTypes]);

    const groupedTrades = useMemo(() => {
        const groups = {};
        (trades || []).forEach(trade => {
            const key = trade.sleeve_name && trade.security_model ? `${trade.sleeve_name}/${trade.security_model}` : (trade.sleeve_name || trade.security_model || trade.symbol || 'Unknown');
            if (!groups[key]) groups[key] = { key, sleeve_name: trade.sleeve_name, security_model: trade.security_model, trades: [], totalValue: 0, target_weight: trade.target_weight, min_target_weight: trade.min_target_weight, max_target_weight: trade.max_target_weight, current_weight: trade.current_weight, resulting_weight: trade.resulting_weight, hasBelowMinimumTrades: false };
            groups[key].trades.push(trade);
            groups[key].totalValue += (trade.trade_value || trade.value || 0);
            if (trade.resulting_weight !== undefined) groups[key].resulting_weight = trade.resulting_weight;
            // Note: below_minimum_target is tracked per-trade for row highlighting,
            // but the group-level badge is computed separately based on portfolio weight
        });
        return Object.values(groups);
    }, [trades]);

    // Build a comprehensive holdings + model securities overview for the collapsible panel
    const portfolioHoldingsOverview = useMemo(() => {
        // Keys already shown in the main suggested trades table — exclude these
        const suggestedTradeKeys = new Set(groupedTrades.map(g => g.key));
        // Also exclude additionalModelGroups so they don't double-appear
        additionalModelGroups.forEach(k => suggestedTradeKeys.add(k));

        // Build account number -> name lookup and restriction lookup
        const accountNameByNumber = {};
        const restrictionBySymbol = {};
        (accountAllocations || []).forEach(a => {
            if (a.accountNumber) accountNameByNumber[a.accountNumber] = a.accountName || a.accountNumber;
        });
        Object.entries(allAccountHoldings || {}).forEach(([accountNumber, holdings]) => {
            if (!Array.isArray(holdings)) return;
            if (!accountNameByNumber[accountNumber] && holdings.length > 0)
                accountNameByNumber[accountNumber] = holdings[0]?.['Account Name'] || accountNumber;
            holdings.forEach(h => {
                const symbol = h.Symbol || h.symbol;
                const restriction = h.Restriction || h.restriction || '';
                if (symbol && restriction && !restrictionBySymbol[symbol]) restrictionBySymbol[symbol] = restriction;
            });
        });

        // Collect model keys from holdings only — securityAllocations may contain models
        // the accounts don't own at all, which would produce empty phantom cards.
        // Securities with no model assignment are grouped under 'Other Holdings' so the
        // user can spot and manually sell assets outside the assigned model.
        const allModelKeys = new Set();
        Object.values(allAccountHoldings || {}).forEach(holdings => {
            if (!Array.isArray(holdings)) return;
            holdings.forEach(h => {
                const rawModelName = h['Security Level Model Name'] || h.modelName || '';
                // Use 'Other Holdings' as a bucket for unassigned securities (cash labels
                // like 'UNASSIGNED CASH' have explicit names and will be filtered below)
                const modelName = rawModelName || 'Other Holdings';
                allModelKeys.add(modelName);
            });
        });

        // Cash pseudo-models should never appear in the holdings panel
        const CASH_KEYS = new Set(['Excess Cash', 'UNASSIGNED CASH', 'UNASSIGNED_CASH', 'Cash', 'CASH']);

        const result = [];
        [...allModelKeys]
            .filter(modelKey => !suggestedTradeKeys.has(modelKey) && !CASH_KEYS.has(modelKey))
            .forEach(modelKey => {
                const matchingAlloc = (securityAllocations || []).find(a => a.name === modelKey);
                const isRestricted = matchingAlloc?.isRestricted || false;

                // Build owned securities map for this model.
                // 'Other Holdings' is a virtual key for securities with no model assignment,
                // so we match on an empty model name in that case.
                const modelKeyForMatch = modelKey === 'Other Holdings' ? '' : modelKey;
                const ownedMap = {};
                Object.entries(allAccountHoldings || {}).forEach(([accountNumber, holdings]) => {
                    if (!Array.isArray(holdings)) return;
                    holdings.forEach(h => {
                        const hSymbol = h.Symbol || h.symbol;
                        const hModel = h['Security Level Model Name'] || h.modelName || '';
                        const hQty = h.Quantity || h.quantity || 0;
                        const hPrice = h.Price || h['Price Per Unit'] || h.price || 0;
                        const hRestriction = h.Restriction || h.restriction || '';
                        if (!hSymbol || hQty === 0 || hModel !== modelKeyForMatch) return;
                        if (!ownedMap[hSymbol]) ownedMap[hSymbol] = { symbol: hSymbol, price: hPrice, restriction: hRestriction, accounts: [] };
                        ownedMap[hSymbol].accounts.push({
                            accountNumber,
                            accountName: accountNameByNumber[accountNumber] || accountNumber,
                            quantity: hQty,
                            value: hQty * hPrice
                        });
                        if (!ownedMap[hSymbol].restriction && hRestriction) ownedMap[hSymbol].restriction = hRestriction;
                        if (hPrice > 0 && !ownedMap[hSymbol].price) ownedMap[hSymbol].price = hPrice;
                    });
                });

                const securities = Object.values(ownedMap).sort((a, b) => a.symbol.localeCompare(b.symbol));
                // Only include models where the accounts actually own something
                if (securities.length === 0) return;

                result.push({ key: modelKey, isRestricted, securities, alloc: matchingAlloc || null });
            });

        return result.sort((a, b) => a.key.localeCompare(b.key));
    }, [groupedTrades, additionalModelGroups, allAccountHoldings, securityAllocations, accountAllocations]);

    const getTradeData = useCallback((groupKey, tradeIndex) => {
        if (allTradesEdits[groupKey] && allTradesEdits[groupKey][tradeIndex]) return allTradesEdits[groupKey][tradeIndex];
        const group = groupedTrades.find(g => g.key === groupKey);
        return group?.trades[tradeIndex] || {};
    }, [allTradesEdits, groupedTrades]);

    const groupTotals = useMemo(() => {
        const totals = {};
        Object.keys(allTradesEdits).forEach(gk => { totals[gk] = (allTradesEdits[gk] || []).reduce((s, t) => s + ((t.quantity || 0) * (t.price || 0)), 0); });
        return totals;
    }, [allTradesEdits]);

    // When groupTotals or securityAllocations change, compute updated allocations
    const _lastAllocPayload = useRef(null);
    useEffect(() => {
        if (typeof onAllocationsChange !== 'function') return;

        const computeUpdated = (allocs) => {
            // Helper to find the matching trade group for an allocation entry.
            // Falls back to checking groupTotals for holdings groups that have manual
            // trades but are not part of the suggested trades (groupedTrades).
            const findMatchingGroup = (name) => {
                const found = groupedTrades.find(g => {
                    if (!name) return false;
                    if (g.security_model && g.security_model === name) return true;
                    if (g.sleeve_name && g.sleeve_name === name) return true;
                    if (g.key === name || g.key === `${g.sleeve_name}/${name}`) return true;
                    return false;
                });
                if (found) return found;
                // Also handle manually-added trades for holdings groups (not in suggested trades)
                if (groupTotals[name] !== undefined) return { key: name };
                return undefined;
            };

            // Determine which groups have been meaningfully edited (trade differs from backend by >1% or >$100)
            // editDeltas[groupKey] = liveTradeValue - backendTradeImpact (non-zero only when edited)
            const editDeltas = {};
            for (const item of (allocs || [])) {
                const name = item.name || item.sleeveName || '';
                const isCashEntry = (name === 'Excess Cash' || name === 'UNASSIGNED CASH' || name === 'UNASSIGNED_CASH');
                if (isCashEntry) continue;
                const group = findMatchingGroup(name);
                if (!group) continue;
                const originalTradeImpact = item.tradeImpact || item.trade_impact || 0;
                const liveTradeValue = groupTotals[group.key] || 0;
                const tradeDiff = Math.abs(liveTradeValue - originalTradeImpact);
                const impactThreshold = Math.abs(originalTradeImpact) * 0.01 || 100;
                if (tradeDiff > impactThreshold) {
                    editDeltas[group.key] = liveTradeValue - originalTradeImpact;
                }
            }

            const hasAnyEdit = Object.keys(editDeltas).length > 0;

            // If nothing has been edited, pass all backend values through unchanged.
            // This ensures afterWeights still sum to ~100% and normalizeAllocWeights is a no-op,
            // keeping the Group Allocation bars consistent with the Weight Allocation bars.
            if (!hasAnyEdit) {
                return (allocs || []).map(item => ({
                    ...item,
                    beforeValue: item.beforeValue || item.before_value || 0,
                    beforeWeight: item.beforeWeight || item.before_weight || 0,
                    tradeImpact: item.tradeImpact || item.trade_impact || 0,
                    afterValue: item.afterValue ?? item.after_value ?? item.beforeValue ?? item.before_value ?? 0,
                    afterWeight: item.afterWeight ?? item.after_weight ?? item.beforeWeight ?? item.before_weight ?? 0,
                }));
            }

            // Some trades were edited — derive total portfolio value for weight recalculation
            let totalPortfolioValue = 0;
            for (const item of (allocs || [])) {
                const bv = item.beforeValue || item.before_value || 0;
                const bw = item.beforeWeight || item.before_weight || 0;
                if (bv > 0 && bw > 0) { totalPortfolioValue = bv / (bw / 100); break; }
            }

            // Cash delta = ONLY the change from user edits (not the original backend trade).
            // groupTotals[key] = qty * price (negative for sells) →
            //   editDelta for a sell that got larger is more negative → more cash → negate to get cash change.
            const cashEditDelta = -Object.values(editDeltas).reduce((s, v) => s + v, 0);

            return (allocs || []).map(item => {
                const name = item.name || item.sleeveName || '';
                const beforeVal = item.beforeValue || item.before_value || 0;
                const beforeWeight = item.beforeWeight || item.before_weight || 0;
                const originalAfterWeight = item.afterWeight ?? item.after_weight ?? beforeWeight;
                const originalAfterValue = item.afterValue ?? item.after_value ?? beforeVal;
                const originalTradeImpact = item.tradeImpact || item.trade_impact || 0;

                // Cash entries: shift afterValue only by the EDIT delta, not the full trade.
                // The backend already priced in the original trade; we only adjust for changes.
                const isCashEntry = (name === 'Excess Cash' || name === 'UNASSIGNED CASH' || name === 'UNASSIGNED_CASH');
                if (isCashEntry) {
                    const newAfterValue = originalAfterValue + cashEditDelta;
                    const newAfterWeight = totalPortfolioValue > 0
                        ? (newAfterValue / totalPortfolioValue) * 100
                        : originalAfterWeight + (cashEditDelta / (originalAfterValue || 1)) * originalAfterWeight;
                    return {
                        ...item,
                        beforeValue: beforeVal,
                        beforeWeight: beforeWeight,
                        tradeImpact: originalTradeImpact + cashEditDelta,
                        afterValue: newAfterValue,
                        afterWeight: newAfterWeight,
                    };
                }

                const matchingGroup = findMatchingGroup(name);
                if (!matchingGroup) {
                    // No matching tradeable group — return backend values unchanged
                    return { ...item, beforeValue: beforeVal, beforeWeight, tradeImpact: originalTradeImpact, afterValue: originalAfterValue, afterWeight: originalAfterWeight };
                }

                const editDelta = editDeltas[matchingGroup.key] || 0;
                if (editDelta === 0) {
                    // This group was not edited — return backend values unchanged
                    return { ...item, beforeValue: beforeVal, beforeWeight, tradeImpact: originalTradeImpact, afterValue: originalAfterValue, afterWeight: originalAfterWeight };
                }

                // Recalculate based on edit delta only
                const afterValue = originalAfterValue + editDelta;
                const afterWeight = totalPortfolioValue > 0
                    ? originalAfterWeight + (editDelta / totalPortfolioValue) * 100
                    : originalAfterWeight;
                return { ...item, beforeValue: beforeVal, beforeWeight, tradeImpact: originalTradeImpact + editDelta, afterValue, afterWeight };
            });
        };

        try {
            const updatedSecurity = computeUpdated(securityAllocations);
            const payload = { security: updatedSecurity, sleeve: updatedSecurity };
            if (areAllocationPayloadsEqual(_lastAllocPayload.current, payload)) return;
            _lastAllocPayload.current = payload;
            onAllocationsChange(payload);
        } catch (err) {
            // No-op on error
        }
    }, [groupTotals, securityAllocations, groupedTrades, onAllocationsChange, areAllocationPayloadsEqual]);

    const groupHoldings = useMemo(() => {
        const holdings = {};
        groupedTrades.forEach(group => {
            const matchingAlloc = (securityAllocations || []).find(alloc => {
                const allocName = alloc.name || '';
                const allocSleeve = alloc.sleeveName || '';
                // Match by security model name (most specific)
                if (allocName && group.security_model && allocName === group.security_model) return true;
                // Match by full key (sleeve/security)
                if (allocName && allocSleeve && group.key === `${allocSleeve}/${allocName}`) return true;
                // Match by name only if no security_model specified
                if (allocName && !group.security_model && group.key === allocName) return true;
                return false;
            });
            holdings[group.key] = matchingAlloc?.beforeValue || 0;
        });
        additionalModelGroups.forEach(modelKey => { const matchingAlloc = (securityAllocations || []).find(alloc => alloc.name === modelKey); holdings[modelKey] = matchingAlloc?.beforeValue || 0; });
        // Include holdings-only groups so renderWeightBar can compute projected weights
        // for trades added from the "Other Holdings & Model Securities" section.
        portfolioHoldingsOverview.forEach(group => {
            if (!(group.key in holdings)) {
                holdings[group.key] = group.securities.reduce(
                    (sum, sec) => sum + sec.accounts.reduce((s, a) => s + (a.value || 0), 0), 0
                );
            }
        });
        return holdings;
    }, [groupedTrades, securityAllocations, additionalModelGroups, portfolioHoldingsOverview]);

    // Compute projected weights and below-minimum status for each group
    const groupProjectedWeights = useMemo(() => {
        const results = {};
        groupedTrades.forEach(group => {
            const matchingAlloc = (securityAllocations || []).find(alloc => {
                const allocName = alloc.name || '';
                const allocSleeve = alloc.sleeveName || '';
                // Match by security model name (most specific)
                if (allocName && group.security_model && allocName === group.security_model) return true;
                // Match by full key (sleeve/security)
                if (allocName && allocSleeve && group.key === `${allocSleeve}/${allocName}`) return true;
                // Match by name only if no security_model specified
                if (allocName && !group.security_model && group.key === allocName) return true;
                return false;
            });

            const currentWeight = matchingAlloc?.beforeWeight ?? group.current_weight ?? 0;
            const target = matchingAlloc?.targetWeight ?? group.target_weight ?? 0;
            const minTarget = matchingAlloc?.minTargetWeight ?? group.min_target_weight ?? 0;
            const tradeValue = groupTotals[group.key] || 0;
            const holdingsValue = groupHoldings[group.key] || 0;
            const originalTradeImpact = matchingAlloc?.tradeImpact || 0;

            // Use backend's afterWeight if trades haven't been meaningfully changed
            let projectedWeight = matchingAlloc?.afterWeight ?? currentWeight;
            const tradeDiff = Math.abs(tradeValue - originalTradeImpact);
            const impactThreshold = Math.abs(originalTradeImpact) * 0.01 || 100;

            if (tradeDiff > impactThreshold && holdingsValue > 0 && currentWeight > 0) {
                const totalPortfolioValue = holdingsValue / (currentWeight / 100);
                if (totalPortfolioValue > 0) {
                    const netTradeDiff = tradeValue - originalTradeImpact;
                    projectedWeight = (matchingAlloc?.afterWeight ?? currentWeight) + (netTradeDiff / totalPortfolioValue) * 100;
                }
            }

            results[group.key] = {
                projectedWeight,
                isProjectedBelowMinimum: projectedWeight < minTarget && minTarget > 0
            };
        });
        return results;
    }, [groupedTrades, securityAllocations, groupTotals, groupHoldings]);

    const renderWeightBar = (group) => {
        const matchingAlloc = (securityAllocations || []).find(alloc => {
            const allocName = alloc.name || '';
            const allocSleeve = alloc.sleeveName || '';
            // Match by security model name (most specific)
            if (allocName && group.security_model && allocName === group.security_model) return true;
            // Match by full key (sleeve/security)
            if (allocName && allocSleeve && group.key === `${allocSleeve}/${allocName}`) return true;
            // Match by name only if no security_model specified
            if (allocName && !group.security_model && group.key === allocName) return true;
            return false;
        });

        const currentWeight = matchingAlloc?.beforeWeight ?? group.current_weight ?? 0;
        const target = matchingAlloc?.targetWeight ?? group.target_weight ?? 0;
        const minTarget = matchingAlloc?.minTargetWeight ?? group.min_target_weight ?? 0;
        const maxTarget = matchingAlloc?.maxTargetWeight ?? group.max_target_weight ?? 0;
        const tradeValue = groupTotals[group.key] || 0;
        const holdingsValue = groupHoldings[group.key] || 0;
        const originalTradeImpact = matchingAlloc?.tradeImpact || 0;
        
        // Use backend's afterWeight if trades haven't been meaningfully changed (within 1% tolerance)
        // This prevents recalculation errors from small rounding differences
        let projectedResulting = matchingAlloc?.afterWeight ?? currentWeight;
        const tradeDiff = Math.abs(tradeValue - originalTradeImpact);
        const impactThreshold = Math.abs(originalTradeImpact) * 0.01 || 100; // 1% tolerance or $100
        
        if (tradeDiff > impactThreshold && holdingsValue > 0 && currentWeight > 0) {
            // User has meaningfully edited trades - recalculate projected weight
            const totalPortfolioValue = holdingsValue / (currentWeight / 100);
            if (totalPortfolioValue > 0) {
                const netTradeDiff = tradeValue - originalTradeImpact;
                projectedResulting = (matchingAlloc?.afterWeight ?? currentWeight) + (netTradeDiff / totalPortfolioValue) * 100;
            }
        }
        
        // Check if the portfolio-level projected weight is below minimum
        const isProjectedBelowMinimum = projectedResulting < minTarget && minTarget > 0;

        if (target === 0 && minTarget === 0 && maxTarget === 0) {
            return (
                <div className="d-flex align-items-center justify-content-between py-2">
                    <div className="d-flex align-items-center gap-3">
                        <i className="bi bi-bar-chart-fill text-primary"></i>
                        <span className="fw-bold small">Trade Summary</span>
                    </div>
                    <div className="d-flex align-items-center gap-4">
                        <div>
                            <small className="text-muted">Current Holdings:</small>
                            <span className="ms-2 fw-bold">{formatCurrency(holdingsValue)}</span>
                        </div>
                        <div>
                            <small className="text-muted">Trade Value:</small>
                            <span className={`ms-2 fw-bold ${tradeValue >= 0 ? 'text-success' : 'text-danger'}`}>{tradeValue >= 0 ? '+' : ''}{formatCurrency(tradeValue)}</span>
                        </div>
                        <div>
                            <small className="text-muted">After Trade:</small>
                            <span className="ms-2 fw-bold">{formatCurrency(holdingsValue + tradeValue)}</span>
                        </div>
                    </div>
                </div>
            );
        }

        const range = maxTarget - minTarget;
        const padding = Math.max(range * 0.3, 2);
        const rangeMin = Math.max(0, minTarget - padding);
        const rangeMax = Math.min(100, maxTarget + padding);
        const rangeSize = rangeMax - rangeMin || 1;
        const minPos = ((minTarget - rangeMin) / rangeSize) * 100;
        const maxPos = ((maxTarget - rangeMin) / rangeSize) * 100;
        const targetPos = ((target - rangeMin) / rangeSize) * 100;
        const currentPos = ((currentWeight - rangeMin) / rangeSize) * 100;
        const projectedPos = ((projectedResulting - rangeMin) / rangeSize) * 100;
        const center = (minPos + maxPos) / 2;
        const gradient = `linear-gradient(to right, #dc3545 0%, #dc3545 ${minPos}%, #28a745 ${center}%, #dc3545 ${maxPos}%, #dc3545 100%)`;

        return (
            <div className="py-1">
                <div className="d-flex align-items-start justify-content-between mb-1">
                    <div className="me-3" style={{ flexShrink: 0 }}>
                        <div className="d-flex align-items-center">
                            <i className="bi bi-speedometer2 me-2 text-primary"></i>
                            <span className="fw-bold small">Weight Allocation</span>
                        </div>
                        <div style={{ fontSize: '0.6rem', marginTop: '2px' }}>
                            <span className="text-muted">Min: {minTarget.toFixed(1)}%</span>
                            <span className="mx-1">|</span>
                            <span className="text-primary fw-bold">Target: {target.toFixed(1)}%</span>
                            <span className="mx-1">|</span>
                            <span className="text-muted">Max: {Math.min(maxTarget, 100).toFixed(1)}%</span>
                        </div>
                    </div>

                    <div className="flex-grow-1 ms-2" style={{ minWidth: '220px', position: 'relative' }}>
                        <div className="d-flex justify-content-between small text-muted mb-1" style={{ fontSize: '0.65rem' }}>
                            <span>{rangeMin.toFixed(1)}%</span>
                            <span>{rangeMax.toFixed(1)}%</span>
                        </div>

                        <div className="position-relative rounded" style={{ height: '16px', background: gradient, border: '1px solid #dee2e6' }}>
                            {/* Min line */}
                            <div className="position-absolute" style={{ left: `${Math.max(0, Math.min(100, minPos))}%`, top: '0', height: '100%', width: '6px', transform: 'translateX(-50%)', cursor: 'pointer', zIndex: 3 }} title={`Min: ${minTarget.toFixed(2)}%`}>
                                <div style={{ position: 'absolute', left: '50%', top: 0, height: '100%', width: '2px', backgroundColor: '#6c757d', transform: 'translateX(-50%)' }} />
                            </div>
                            {/* Max line */}
                            <div className="position-absolute" style={{ left: `${Math.max(0, Math.min(100, maxPos))}%`, top: '0', height: '100%', width: '6px', transform: 'translateX(-50%)', cursor: 'pointer', zIndex: 3 }} title={`Max: ${Math.min(maxTarget, 100).toFixed(2)}%`}>
                                <div style={{ position: 'absolute', left: '50%', top: 0, height: '100%', width: '2px', backgroundColor: '#6c757d', transform: 'translateX(-50%)' }} />
                            </div>
                            {/* Target line */}
                            <div className="position-absolute" style={{ left: `${Math.max(0, Math.min(100, targetPos))}%`, top: '-2px', height: 'calc(100% + 4px)', width: '6px', transform: 'translateX(-50%)', cursor: 'pointer', zIndex: 3 }} title={`Target: ${target.toFixed(2)}%`}>
                                <div style={{ position: 'absolute', left: '50%', top: 0, height: '100%', width: '2px', backgroundColor: '#0d6efd', transform: 'translateX(-50%)', borderRadius: '1px' }} />
                            </div>

                            <div className="position-absolute" style={{ left: `${Math.max(0, Math.min(100, projectedPos))}%`, top: '-6px', transform: 'translateX(-50%)' }} title={`Projected: ${projectedResulting.toFixed(2)}%`}>
                                <div style={{ width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: `6px solid ${projectedResulting >= minTarget && projectedResulting <= maxTarget ? '#198754' : '#dc3545'}` }} />
                            </div>

                            <div className="position-absolute text-center" style={{ left: `${Math.max(0, Math.min(100, projectedPos))}%`, top: '-20px', transform: 'translateX(-50%)', fontSize: '0.75rem', color: projectedResulting >= minTarget && projectedResulting <= maxTarget ? '#198754' : '#dc3545', background: 'rgba(255,255,255,0.95)', padding: '0 4px', borderRadius: '3px', lineHeight: '1', zIndex: 2 }} aria-hidden="true">
                                {projectedResulting.toFixed(2)}%
                            </div>

                            <div className="position-absolute" style={{ left: `${Math.max(0, Math.min(100, currentPos))}%`, bottom: '-6px', transform: 'translateX(-50%)' }} title={`Current: ${currentWeight.toFixed(2)}%`}>
                                <div style={{ width: 0, height: 0, borderLeft: '4px solid transparent', borderRight: '4px solid transparent', borderBottom: '6px solid #6c757d' }} />
                            </div>

                            <div className="position-absolute text-center" style={{ left: `${Math.max(0, Math.min(100, currentPos))}%`, bottom: '-20px', transform: 'translateX(-50%)', fontSize: '0.75rem', color: '#6c757d', background: 'rgba(255,255,255,0.95)', padding: '0 4px', borderRadius: '3px', lineHeight: '1', zIndex: 2 }} aria-hidden="true">
                                {currentWeight.toFixed(2)}%
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderTradeReasoningModal = () => {
        if (!reasoningModalData) return null;
        const { trade, groupKey } = reasoningModalData;
        const reasoning = trade.reasoning || trade.trade_reasoning;
        if (!reasoning) return null;

        return (
            <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={() => setReasoningModalData(null)}>
                <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title"><i className="bi bi-lightbulb text-warning me-2"></i>Trade Reasoning - {groupKey}</h5>
                            <button type="button" className="btn-close" onClick={() => setReasoningModalData(null)}></button>
                        </div>
                        <div className="modal-body">
                            {reasoning.explanation && (
                                <div className="mb-4">
                                    <h6 className="mb-2"><i className="bi bi-lightbulb text-warning me-2"></i>Why This Trade</h6>
                                    <div className="alert alert-info mb-0" style={{ fontSize: '0.95rem' }}>{reasoning.explanation}</div>
                                </div>
                            )}

                            {reasoning.reasoning && (
                                <div>
                                    <h6 className="mb-3"><i className="bi bi-gear text-secondary me-2"></i>Algorithmic Details</h6>
                                    <div className="row g-3" style={{ fontSize: '0.9rem' }}>
                                        {reasoning.reasoning.account_selection && (
                                            <div className="col-lg-4 col-md-6 col-12">
                                                <div className="card h-100 shadow-sm">
                                                    <div className="card-body">
                                                        <h6 className="card-title text-primary mb-2"><i className="bi bi-person-circle me-2"></i>Account Selection</h6>
                                                        <p className="mb-2">{reasoning.reasoning.account_selection.reason}</p>
                                                        <div className="small text-muted">
                                                            {reasoning.reasoning.account_selection.account_type && (<div>Type: <strong>{reasoning.reasoning.account_selection.account_type}</strong></div>)}
                                                            {typeof reasoning.reasoning.account_selection.current_cash === 'number' && (<div>Cash: <strong>{formatCurrency(reasoning.reasoning.account_selection.current_cash)}</strong></div>)}
                                                            {typeof reasoning.reasoning.account_selection.deficit === 'number' && (<div>Deficit: <strong className="text-danger">{formatCurrency(reasoning.reasoning.account_selection.deficit)}</strong></div>)}
                                                            {typeof reasoning.reasoning.account_selection.excess === 'number' && (<div>Excess: <strong className="text-success">{formatCurrency(reasoning.reasoning.account_selection.excess)}</strong></div>)}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {reasoning.reasoning.model_selection && (
                                            <div className="col-lg-4 col-md-6 col-12">
                                                <div className="card h-100 shadow-sm">
                                                    <div className="card-body">
                                                        <h6 className="card-title text-success mb-2"><i className="bi bi-diagram-3 me-2"></i>Model Selection</h6>
                                                        <p className="mb-2">{reasoning.reasoning.model_selection.reason}</p>
                                                        <div className="small text-muted">
                                                            {reasoning.reasoning.model_selection.current_value !== undefined && (<div>Current: <strong>{formatCurrency(reasoning.reasoning.model_selection.current_value)}{reasoning.reasoning.model_selection.current_weight !== undefined && (<> ({reasoning.reasoning.model_selection.current_weight.toFixed(2)}%)</>)}</strong></div>)}
                                                            {reasoning.reasoning.model_selection.target_value !== undefined && (<div>Target: <strong>{formatCurrency(reasoning.reasoning.model_selection.target_value)}{reasoning.reasoning.model_selection.target_weight !== undefined && (<> ({reasoning.reasoning.model_selection.target_weight.toFixed(2)}%)</>)}</strong></div>)}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {reasoning.reasoning.security_selection && (
                                            <div className="col-lg-4 col-md-6 col-12">
                                                <div className="card h-100 shadow-sm">
                                                    <div className="card-body">
                                                        <h6 className="card-title text-info mb-2"><i className="bi bi-graph-up me-2"></i>Security Selection</h6>
                                                        <p className="mb-2">{reasoning.reasoning.security_selection.reason}</p>
                                                        <div className="small text-muted">
                                                            {reasoning.reasoning.security_selection.ranking !== undefined && (<div>Rank: <strong>#{reasoning.reasoning.security_selection.ranking}</strong></div>)}
                                                            {reasoning.reasoning.security_selection.current_holdings !== undefined && (<div>Holdings: <strong>{reasoning.reasoning.security_selection.current_holdings} shares</strong></div>)}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" onClick={() => setReasoningModalData(null)}>Close</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // When viewing queued trades, only show groups that are actually in the queued data
    const isViewingQueuedTrades = !!(showQueuedTrades && queuedTradesData?.trades);
    const visibleGroups = isViewingQueuedTrades
        ? groupedTrades.filter(g => allTradesEdits[g.key] !== undefined)
        : groupedTrades;
    // Orphan queued groups: in allTradesEdits but not matched by any suggested trade group or additionalModelGroups
    const visibleGroupKeys = new Set(visibleGroups.map(g => g.key));
    const orphanQueuedKeys = isViewingQueuedTrades
        ? Object.keys(allTradesEdits).filter(k => !visibleGroupKeys.has(k) && !additionalModelGroups.includes(k))
        : [];
    // Count of queued trades across all visible groups
    const queuedTradeCount = isViewingQueuedTrades
        ? Object.values(allTradesEdits).reduce((s, arr) => s + arr.length, 0)
        : trades.length;
    const queuedGroupCount = isViewingQueuedTrades
        ? Object.keys(allTradesEdits).length
        : groupedTrades.length;

    const hasNoSuggestedTrades = !isViewingQueuedTrades && (!trades || trades.length === 0);

    return (
        <div className="suggested-trades-table">
            {hasNoSuggestedTrades && (
                <div className="alert alert-info mb-2 py-2"><i className="bi bi-info-circle me-2"></i>No trades needed for this account family. You can still add trades manually below.</div>
            )}
            <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0"><i className="bi bi-arrow-left-right me-2"></i>Suggested Trades ({queuedTradeCount})</h6>
                <small className="text-muted">{queuedGroupCount} security group{queuedGroupCount !== 1 ? 's' : ''}</small>
            </div>

            {visibleGroups.map(group => {
                const isExpanded = expandedRows.has(group.key);
                const tradesForGroup = allTradesEdits[group.key] || group.trades;
                const hasReasoning = tradesForGroup.some(t => t.reasoning || t.trade_reasoning || t.group_reasoning_summary);
                // Use portfolio-level projected weight to determine if group is below minimum
                const groupProjection = groupProjectedWeights[group.key] || {};
                const isPortfolioBelowMinimum = groupProjection.isProjectedBelowMinimum;

                return (
                    <div key={group.key} className="mb-3">
                        <div className="card" style={{ border: isPortfolioBelowMinimum ? '2px solid #ffc107' : '1px solid #dee2e6', cursor: 'pointer' }}>
                            <div className={`card-header py-2 px-3 d-flex justify-content-between align-items-center ${isPortfolioBelowMinimum ? 'bg-warning bg-opacity-25' : 'bg-light'}`} onClick={() => { const ns = new Set(expandedRows); if (ns.has(group.key)) ns.delete(group.key); else ns.add(group.key); setExpandedRows(ns); }}>
                                <div className="d-flex align-items-center gap-3">
                                    <i className={`bi bi-chevron-${isExpanded ? 'down' : 'right'}`}></i>
                                    <span className="fw-bold">{group.key}</span>
                                    <span className="badge bg-secondary">{tradesForGroup.length} trade{tradesForGroup.length !== 1 ? 's' : ''}</span>
                                    {isPortfolioBelowMinimum && (
                                        <span className="badge bg-warning text-dark" title="Portfolio weight will be below minimum target after trades">
                                            <i className="bi bi-exclamation-triangle-fill me-1"></i>
                                            Below Min
                                        </span>
                                    )}
                                </div>
                                <div className="d-flex align-items-center gap-3">
                                    <span className="text-muted small">Total:</span>
                                    <strong className={(groupTotals[group.key] !== undefined ? groupTotals[group.key] : group.totalValue) < 0 ? 'text-danger' : 'text-success'}>{formatCurrency(groupTotals[group.key] !== undefined ? groupTotals[group.key] : group.totalValue)}</strong>
                                    {hasReasoning && (<button className="btn btn-sm btn-outline-warning" title="View trade reasoning" onClick={(e) => { e.stopPropagation(); setReasoningModalData({ trade: tradesForGroup[0], groupKey: group.key }); }}><i className="bi bi-lightbulb"></i></button>)}
                                </div>
                            </div>

                            {isExpanded && (
                                <div className="card-body py-3 px-3" style={{ backgroundColor: '#f8f9fa' }}>
                                    <div className="mb-3 p-2 bg-white rounded border">{renderWeightBar(group)}</div>

                                    <TradeConfigurationCard
                                        title="Trade Configuration"
                                        groupKey={group.key}
                                        tradesList={allTradesEdits[group.key] || group.trades}
                                        getTradeData={getTradeData}
                                        updateTradeField={updateTradeField}
                                        addTradeRow={addTradeRow}
                                        removeTradeRow={removeTradeRow}
                                        resetGroupTrades={resetGroupTrades}
                                        availableAccounts={availableAccounts}
                                        symbolsByGroup={symbolsByGroup}
                                        holdingsLookup={holdingsLookup}
                                        formatCurrency={formatCurrency}
                                        groupAlloc={(securityAllocations || []).find(alloc => {
                                            const allocName = alloc.name || '';
                                            const allocSleeve = alloc.sleeveName || '';
                                            if (allocName && group.security_model && allocName === group.security_model) return true;
                                            if (allocName && allocSleeve && group.key === `${allocSleeve}/${allocName}`) return true;
                                            if (allocName && !group.security_model && group.key === allocName) return true;
                                            return false;
                                        }) || null}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}

            {orphanQueuedKeys.map(groupKey => {
                const isExpanded = expandedRows.has(groupKey);
                const tradesForGroup = allTradesEdits[groupKey] || [];
                const groupTotal = groupTotals[groupKey] || 0;
                return (
                    <div key={groupKey} className="mb-3">
                        <div className="card" style={{ border: '1px solid #dee2e6', cursor: 'pointer' }}>
                            <div className="card-header py-2 px-3 d-flex justify-content-between align-items-center bg-light" onClick={() => { const ns = new Set(expandedRows); if (ns.has(groupKey)) ns.delete(groupKey); else ns.add(groupKey); setExpandedRows(ns); }}>
                                <div className="d-flex align-items-center gap-3">
                                    <i className={`bi bi-chevron-${isExpanded ? 'down' : 'right'}`}></i>
                                    <span className="fw-bold">{groupKey}</span>
                                    <span className="badge bg-secondary">{tradesForGroup.length} trade{tradesForGroup.length !== 1 ? 's' : ''}</span>
                                </div>
                                <div className="d-flex align-items-center gap-3">
                                    <span className="text-muted small">Total:</span>
                                    <strong className={groupTotal < 0 ? 'text-danger' : 'text-success'}>{formatCurrency(groupTotal)}</strong>
                                </div>
                            </div>
                            {isExpanded && (
                                <div className="card-body py-3 px-3" style={{ backgroundColor: '#f8f9fa' }}>
                                    <TradeConfigurationCard
                                        title="Trade Configuration"
                                        groupKey={groupKey}
                                        tradesList={tradesForGroup}
                                        getTradeData={getTradeData}
                                        updateTradeField={updateTradeField}
                                        addTradeRow={addTradeRow}
                                        removeTradeRow={removeTradeRow}
                                        resetGroupTrades={resetGroupTrades}
                                        availableAccounts={availableAccounts}
                                        symbolsByGroup={symbolsByGroup}
                                        holdingsLookup={holdingsLookup}
                                        formatCurrency={formatCurrency}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}

            {additionalModelGroups.length > 0 && (
                <div className="mt-4">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0 text-primary"><i className="bi bi-plus-square me-2"></i>Additional Trades ({additionalModelGroups.length} model{additionalModelGroups.length !== 1 ? 's' : ''})</h6>
                    </div>

                    {additionalModelGroups.map(modelKey => {
                        const isExpanded = expandedRows.has(modelKey);
                        const tradesForGroup = allTradesEdits[modelKey] || [];
                        const modelInfo = (securityAllocations || []).find(a => a.name === modelKey) || {};

                        return (
                            <div key={modelKey} className="mb-3">
                                <div className="card" style={{ border: '2px solid #0d6efd', cursor: 'pointer' }}>
                                    <div className="card-header bg-primary bg-opacity-10 py-2 px-3 d-flex justify-content-between align-items-center" onClick={() => { const ns = new Set(expandedRows); if (ns.has(modelKey)) ns.delete(modelKey); else ns.add(modelKey); setExpandedRows(ns); }}>
                                        <div className="d-flex align-items-center gap-3">
                                            <i className={`bi bi-chevron-${isExpanded ? 'down' : 'right'}`}></i>
                                            <span className="fw-bold text-primary">{modelKey}</span>
                                            <span className="badge bg-primary">{tradesForGroup.length} trade{tradesForGroup.length !== 1 ? 's' : ''}</span>
                                            <span className="badge bg-info">Added</span>
                                        </div>
                                        <div className="d-flex align-items-center gap-3">
                                            <span className="text-muted small">Total:</span>
                                            <strong className={(groupTotals[modelKey] || 0) < 0 ? 'text-danger' : 'text-success'}>{formatCurrency(groupTotals[modelKey] || 0)}</strong>
                                            <button className="btn btn-sm btn-outline-danger" title="Remove this model" onClick={(e) => { e.stopPropagation(); removeModelGroup(modelKey); }}><i className="bi bi-x-circle"></i></button>
                                        </div>
                                    </div>

                                    {isExpanded && (
                                        <div className="card-body py-3 px-3" style={{ backgroundColor: '#f0f7ff' }}>
                                            <div className="mb-3 p-2 bg-white rounded border">{renderWeightBar({ key: modelKey, security_model: modelKey, sleeve_name: modelInfo.sleeveName, current_weight: modelInfo.beforeWeight || 0, target_weight: modelInfo.targetWeight || 0 })}</div>

                                            <TradeConfigurationCard
                                                title="Trade Configuration"
                                                groupKey={modelKey}
                                                tradesList={allTradesEdits[modelKey] || []}
                                                getTradeData={getTradeData}
                                                updateTradeField={updateTradeField}
                                                addTradeRow={addTradeRow}
                                                removeTradeRow={removeTradeRow}
                                                availableAccounts={availableAccounts}
                                                symbolsByGroup={symbolsByModel}
                                                holdingsLookup={holdingsLookup}
                                                formatCurrency={formatCurrency}
                                                groupAlloc={Object.keys(modelInfo).length > 0 ? modelInfo : null}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Holdings & Model Securities Overview - collapsible panel */}
            <div className="mt-3 mb-2">
                <div className="card" style={{ border: '1px solid #dee2e6' }}>
                    <div
                        className="card-header py-2 px-3 d-flex justify-content-between align-items-center bg-light"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setShowHoldingsOverview(p => !p)}
                    >
                        <div className="d-flex align-items-center gap-2">
                            <i className={`bi bi-chevron-${showHoldingsOverview ? 'down' : 'right'}`}></i>
                            <i className="bi bi-briefcase text-primary"></i>
                            <span className="fw-bold ms-1">Other Holdings &amp; Model Securities</span>
                            <span className="badge bg-secondary ms-1">{portfolioHoldingsOverview.reduce((s, g) => s + g.securities.length, 0)} securities</span>
                        </div>
                        <small className="text-muted">{portfolioHoldingsOverview.length} model group{portfolioHoldingsOverview.length !== 1 ? 's' : ''}</small>
                    </div>

                    {showHoldingsOverview && (() => {
                        const otherHoldingsGroup = portfolioHoldingsOverview.find(g => g.key === 'Other Holdings');
                        const normalGroups = portfolioHoldingsOverview.filter(g => !g.isRestricted && g.key !== 'Other Holdings');
                        const restrictedGroups = portfolioHoldingsOverview.filter(g => g.isRestricted);

                        const renderGroupCard = (group) => {
                            const isOtherHoldings = group.key === 'Other Holdings';
                            const groupObj = { key: group.key, security_model: group.key, sleeve_name: group.alloc?.sleeveName };
                            const alloc = group.alloc;
                            const hasRestrictions = group.securities.some(s => s.restriction);
                            const isGroupExpanded = expandedHoldingsGroups.has(group.key);
                            const isTradeCardOpen = showTradeCardForHoldings.has(group.key);
                            const toggleGroup = () => setExpandedHoldingsGroups(prev => {
                                const next = new Set(prev);
                                if (next.has(group.key)) next.delete(group.key); else next.add(group.key);
                                return next;
                            });
                            const toggleTradeCard = (e) => {
                                e.stopPropagation();
                                setShowTradeCardForHoldings(prev => {
                                    const next = new Set(prev);
                                    if (next.has(group.key)) {
                                        next.delete(group.key);
                                    } else {
                                        next.add(group.key);
                                        // Auto-expand the group card and seed an empty SELL trade row if none exist
                                        setExpandedHoldingsGroups(p => new Set(p).add(group.key));
                                        if (!(allTradesEdits[group.key]?.length > 0)) {
                                            addTradeRow(group.key);
                                            // Pre-set action to SELL for out-of-model assets
                                            if (isOtherHoldings) {
                                                setTimeout(() => updateTradeField(group.key, 0, 'action', 'SELL'), 0);
                                            }
                                        }
                                    }
                                    return next;
                                });
                            };
                            // Determine border / header colour based on group type
                            const borderColor = group.isRestricted ? '#dc3545' : isOtherHoldings ? '#fd7e14' : '#dee2e6';
                            const borderWidth = (group.isRestricted || isOtherHoldings) ? '2px' : '1px';
                            const headerBg = group.isRestricted ? 'bg-danger bg-opacity-10' : isOtherHoldings ? 'bg-warning bg-opacity-10' : 'bg-light';
                            return (
                                <div key={group.key} className="mb-2 rounded border" style={{ borderColor, borderWidth, borderStyle: 'solid', overflow: 'hidden' }}>
                                    <div
                                        className={`d-flex align-items-center px-3 py-2 ${headerBg}`}
                                        style={{ cursor: 'pointer' }}
                                        onClick={toggleGroup}
                                    >
                                        <i className={`bi bi-chevron-${isGroupExpanded ? 'down' : 'right'} me-2`}></i>
                                        <span className="fw-bold">{group.key}</span>
                                        {group.isRestricted && (
                                            <span className="badge bg-danger ms-2">
                                                <i className="bi bi-exclamation-octagon-fill me-1"></i>RESTRICTED MODEL
                                            </span>
                                        )}
                                        {isOtherHoldings && (
                                            <span className="badge bg-warning text-dark ms-2">
                                                <i className="bi bi-box-arrow-left me-1"></i>Out of Model
                                            </span>
                                        )}
                                        {hasRestrictions && !group.isRestricted && !isOtherHoldings && (
                                            <span className="badge bg-warning text-dark ms-2">
                                                <i className="bi bi-exclamation-triangle-fill me-1"></i>Has Restrictions
                                            </span>
                                        )}
                                        <div className="d-flex align-items-center gap-3 ms-auto">
                                            {alloc && (
                                                <div className="d-flex gap-3 small text-muted">
                                                    <span>Current: <strong className="text-dark">{(alloc.beforeWeight ?? 0).toFixed(2)}%</strong></span>
                                                    <span>Target: <strong className="text-primary">{(alloc.targetWeight ?? 0).toFixed(2)}%</strong></span>
                                                    <span>Min: <strong>{(alloc.minTargetWeight ?? 0).toFixed(2)}%</strong></span>
                                                    <span>Max: <strong>{(alloc.maxTargetWeight ?? 0).toFixed(2)}%</strong></span>
                                                </div>
                                            )}
                                            <button
                                                className={`btn btn-sm ${isTradeCardOpen ? 'btn-outline-secondary' : 'btn-success'}`}
                                                onClick={toggleTradeCard}
                                                title={isTradeCardOpen ? 'Hide trade entry' : 'Add a trade for this model'}
                                            >
                                                <i className={`bi ${isTradeCardOpen ? 'bi-x-circle' : 'bi-plus-circle-fill'} me-1`}></i>
                                                {isTradeCardOpen ? 'Hide Trades' : 'Add Trade'}
                                            </button>
                                        </div>
                                    </div>
                                    {isGroupExpanded && (
                                        <div className="p-3 bg-white">
                                            {alloc && (
                                                <div className="mb-3 p-2 bg-light rounded border">
                                                    {renderWeightBar(groupObj)}
                                                </div>
                                            )}
                                            {isTradeCardOpen && (
                                                <div className="mb-3">
                                                    <TradeConfigurationCard
                                                        title="Trade Configuration"
                                                        groupKey={group.key}
                                                        tradesList={allTradesEdits[group.key] || []}
                                                        getTradeData={getTradeData}
                                                        updateTradeField={updateTradeField}
                                                        addTradeRow={addTradeRow}
                                                        removeTradeRow={removeTradeRow}
                                                        resetGroupTrades={resetGroupTrades}
                                                        availableAccounts={availableAccounts}
                                                        symbolsByGroup={symbolsByModel}
                                                        holdingsLookup={holdingsLookup}
                                                        formatCurrency={formatCurrency}
                                                        groupAlloc={group.alloc || null}
                                                    />
                                                </div>
                                            )}
                                            {group.securities.length > 0 ? (() => {
                                                // Flatten securities into per-row objects so every column is independently sortable
                                                const flatRows = [];
                                                group.securities.forEach(sec => {
                                                    if (sec.accounts.length === 0) {
                                                        flatRows.push({ symbol: sec.symbol, accountName: '', accountNumber: '', quantity: null, value: null, price: sec.price, restriction: sec.restriction, notHeld: true });
                                                    } else {
                                                        sec.accounts.forEach(acct => {
                                                            flatRows.push({ symbol: sec.symbol, accountName: acct.accountName, accountNumber: acct.accountNumber, quantity: acct.quantity, value: acct.value, price: sec.price, restriction: sec.restriction, notHeld: false });
                                                        });
                                                    }
                                                });
                                                const sort = holdingsSortState[group.key] || { col: 'symbol', dir: 'asc' };
                                                const sortedRows = [...flatRows].sort((a, b) => {
                                                    let av, bv;
                                                    if (sort.col === 'symbol')      { av = a.symbol;           bv = b.symbol; }
                                                    else if (sort.col === 'account')     { av = a.accountName;      bv = b.accountName; }
                                                    else if (sort.col === 'qty')         { av = a.quantity ?? -Infinity; bv = b.quantity ?? -Infinity; }
                                                    else if (sort.col === 'value')       { av = a.value ?? -Infinity;   bv = b.value ?? -Infinity; }
                                                    else if (sort.col === 'price')       { av = a.price ?? -Infinity;   bv = b.price ?? -Infinity; }
                                                    else if (sort.col === 'restriction') { av = a.restriction || '';    bv = b.restriction || ''; }
                                                    if (typeof av === 'string') return sort.dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
                                                    return sort.dir === 'asc' ? av - bv : bv - av;
                                                });
                                                const sortIcon = (col) => sort.col !== col
                                                    ? <i className="bi bi-arrow-down-up ms-1 text-muted opacity-50" style={{ fontSize: '0.7rem' }}></i>
                                                    : sort.dir === 'asc'
                                                        ? <i className="bi bi-sort-up ms-1" style={{ fontSize: '0.7rem' }}></i>
                                                        : <i className="bi bi-sort-down ms-1" style={{ fontSize: '0.7rem' }}></i>;
                                                const toggleSort = (col) => setHoldingsSortState(prev => {
                                                    const cur = prev[group.key] || { col: 'symbol', dir: 'asc' };
                                                    return { ...prev, [group.key]: { col, dir: cur.col === col && cur.dir === 'asc' ? 'desc' : 'asc' } };
                                                });
                                                const thStyle = { cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' };
                                                return (
                                                    <table className="table table-sm table-bordered mb-0" style={{ fontSize: '0.82rem' }}>
                                                        <thead className="table-light">
                                                            <tr>
                                                                <th style={thStyle} onClick={() => toggleSort('symbol')}>Symbol{sortIcon('symbol')}</th>
                                                                <th style={thStyle} onClick={() => toggleSort('account')}>Account{sortIcon('account')}</th>
                                                                <th className="text-end" style={thStyle} onClick={() => toggleSort('qty')}>Qty{sortIcon('qty')}</th>
                                                                <th className="text-end" style={thStyle} onClick={() => toggleSort('value')}>Value{sortIcon('value')}</th>
                                                                <th className="text-end" style={thStyle} onClick={() => toggleSort('price')}>Price{sortIcon('price')}</th>
                                                                <th style={thStyle} onClick={() => toggleSort('restriction')}>Restriction{sortIcon('restriction')}</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {sortedRows.map((row, ri) => row.notHeld ? (
                                                                <tr key={`${row.symbol}-notheld`} className="text-muted">
                                                                    <td><strong>{row.symbol}</strong></td>
                                                                    <td colSpan={3} className="fst-italic">Not currently held</td>
                                                                    <td className="text-end">{row.price > 0 ? formatCurrency(row.price) : '—'}</td>
                                                                    <td>{row.restriction ? <span className="badge bg-danger">{row.restriction}</span> : '—'}</td>
                                                                </tr>
                                                            ) : (
                                                                <tr key={`${row.symbol}-${row.accountNumber}-${ri}`}>
                                                                    <td><strong>{row.symbol}</strong></td>
                                                                    <td>{row.accountName}</td>
                                                                    <td className="text-end">{Number(row.quantity).toLocaleString()}</td>
                                                                    <td className="text-end">{formatCurrency(row.value)}</td>
                                                                    <td className="text-end">{row.price > 0 ? formatCurrency(row.price) : '—'}</td>
                                                                    <td>{row.restriction ? <span className="badge bg-danger">{row.restriction}</span> : <span className="text-muted">—</span>}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                );
                                            })() : (
                                                <div className="text-muted fst-italic small">No holdings for this model.</div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        };

                        return (
                            <div className="card-body py-3 px-3" style={{ backgroundColor: '#f8f9fa' }}>
                                {portfolioHoldingsOverview.length === 0 && (
                                    <div className="text-muted fst-italic small">No holdings data available.</div>
                                )}

                                {/* Normal (non-restricted) model groups */}
                                {normalGroups.map(renderGroupCard)}

                                {/* Other Holdings sub-panel — assets the client owns that are not part of any model */}
                                {otherHoldingsGroup && (
                                    <div className="mt-3 rounded border" style={{ border: '2px solid #fd7e14', overflow: 'hidden' }}>
                                        <div
                                            className="d-flex align-items-center px-3 py-2 bg-warning bg-opacity-10"
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => setExpandedHoldingsGroups(prev => {
                                                const next = new Set(prev);
                                                if (next.has('Other Holdings')) next.delete('Other Holdings'); else next.add('Other Holdings');
                                                return next;
                                            })}
                                        >
                                            <i className={`bi bi-chevron-${expandedHoldingsGroups.has('Other Holdings') ? 'down' : 'right'} me-2 text-warning`}></i>
                                            <i className="bi bi-box-arrow-left text-warning me-2"></i>
                                            <span className="fw-bold text-warning">Other Holdings</span>
                                            <span className="badge bg-warning text-dark ms-2">{otherHoldingsGroup.securities.length} securities</span>
                                            <span className="ms-3 small text-muted fst-italic">Assets outside the assigned model — typically candidates for manual SELL trades</span>
                                        </div>
                                        {expandedHoldingsGroups.has('Other Holdings') && (
                                            <div className="p-2" style={{ backgroundColor: '#fffdf0' }}>
                                                {renderGroupCard(otherHoldingsGroup)}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Restricted Models sub-panel — at the bottom */}
                                {restrictedGroups.length > 0 && (
                                    <div className="mt-3 rounded border" style={{ border: '2px solid #dc3545', overflow: 'hidden' }}>
                                        <div
                                            className="d-flex align-items-center px-3 py-2 bg-danger bg-opacity-10"
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => setShowRestrictedModels(p => !p)}
                                        >
                                            <i className={`bi bi-chevron-${showRestrictedModels ? 'down' : 'right'} me-2 text-danger`}></i>
                                            <i className="bi bi-exclamation-octagon-fill text-danger me-2"></i>
                                            <span className="fw-bold text-danger">Restricted Models</span>
                                            <span className="badge bg-danger ms-2">{restrictedGroups.length}</span>
                                        </div>
                                        {showRestrictedModels && (
                                            <div className="p-2" style={{ backgroundColor: '#fff5f5' }}>
                                                {restrictedGroups.map(renderGroupCard)}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })()}
                </div>
            </div>

            {renderTradeReasoningModal()}
        </div>
    );
};

SuggestedTradesTable.propTypes = {
    trades: PropTypes.array.isRequired,
    formatCurrency: PropTypes.func.isRequired,
    accountAllocations: PropTypes.array,
    securityAllocations: PropTypes.array,
    allAccountHoldings: PropTypes.object,
    onQueueTrades: PropTypes.func,
    queuedTradesData: PropTypes.object,
    showQueuedTrades: PropTypes.bool,
    onQueueTriggerReady: PropTypes.func,
    onAllocationsChange: PropTypes.func,
    onTradesChange: PropTypes.func
};

export default SuggestedTradesTable;

